<template>
    <div class="ai-copilot" id="ai-copilot">
        <RadialMenu :menu-items="items" :size="450" :loading="isLoading" @close="closeRadialMenu" @clicked="radialMenuClicked" />
    </div>
</template>

<script>
import CoPilot from "../../../../../utils/CoPilot";
import RadialMenu from "../../../../ui/radial-menu/RadialMenu.vue";
import { mapState } from "vuex";

export default {
    name: "AICopilot",
    components: { RadialMenu },
    props: {
        payload: {
            type: Object,
            default: () => ({}),
        },
        contact: {
            type: Object,
            default: () => ({}),
        },
        type: {
            type: String,
            default: "message",
        },
        mailText: {
            type: String,
            default: "",
        },
        mailDetails: {
            type: String,
            default: "",
        },
        ticketText: {
            type: String,
            default: "",
        },
        ticketMessage: {
            type: String,
            default: "",
        },
        osrPayload: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            language: (state) => state.language.language,
        }),
        /** CoPilot şablonlarında user.departments için subjects ile aynı hizada (API User şeması subjects kullanır). */
        copilotUserPayload() {
            var u = this.user;
            if (!u) return undefined;
            var copy = Object.assign({}, u);
            if (!Array.isArray(copy.departments) && Array.isArray(copy.subjects)) {
                copy.departments = copy.subjects;
            }
            return copy;
        },
        /** CoPilot context: socket tablet’ten /api/messages/copilot/:conversationId ile mesajları yükler */
        copilotContextOpts() {
            var o = {
                user: this.copilotUserPayload,
                visitor: this.contact && this.contact._id ? this.contact : undefined,
            };
            if (this.contact && this.contact.lastConversation && this.contact.lastConversation._id) {
                o.conversationId = this.contact.lastConversation._id;
            }
            return o;
        },
    },
    data() {
        const items = [
            {
                id: "fix-grammar",
                title: this.$t("message.fixGrammar"),
                icon: "#fix-grammar",
            },
            {
                id: "long-sentence",
                title: this.$t("message.longSentence"),
                icon: "#long-sentence",
            },
            {
                id: "change-tone",
                title: this.$t("message.changeTone"),
                icon: "#change-tone",
                items: [
                    {
                        id: "change-tone-to-professional",
                        title: this.$t("message.professional"),
                        icon: "#change-tone",
                    },
                    {
                        id: "change-tone-to-casual",
                        title: this.$t("message.casual"),
                        icon: "#change-tone",
                    },
                    {
                        id: "change-tone-to-straightforward",
                        title: this.$t("message.straightForward"),
                        icon: "#change-tone",
                    },
                    {
                        id: "change-tone-to-confident",
                        title: this.$t("message.confident"),
                        icon: "#change-tone",
                    },
                    {
                        id: "change-tone-to-friendly",
                        title: this.$t("message.friendly"),
                        icon: "#change-tone",
                    },
                ],
            },
        ];

        if (this.type !== "mail" && this.type !== "ticket") {
            items.push(
                {
                    id: "ai-response",
                    title: this.$t("message.aiResponse"),
                    icon: "#ai-response",
                },
                {
                    id: "prompt-message",
                    title: this.$t("message.runPrompt"),
                    icon: "#prompt",
                },
            );
        }
        if (this.type === "ticket") {
            items.push(
                {
                    id: "ticket-ai-response",
                    title: this.$t("message.aiResponse"),
                    icon: "#ai-response",
                },
            );
        }
        if (this.type === "mail") {
            items.push(
                {
                    id: "mail-ai-response",
                    title: this.$t("message.aiResponse"),
                    icon: "#ai-response",
                },
                {
                    id: "mail-prompt-message",
                    title: this.$t("message.runPrompt"),
                    icon: "#prompt",
                },
            );
        }

        items.push({
            id: "translate",
            title: this.$t("message.aiTranslate"),
            icon: "#translate",
            items: [
                {
                    id: "translate-to-my-language",
                    title: this.$t("message.myLanguage"),
                    icon: "#translate",
                },
                {
                    id: "translate-to-its-language",
                    title: this.$t("message.contactLanguage"),
                    icon: "#translate",
                },
                {
                    id: "other",
                    title: this.$t("message.other"),
                    icon: "#translate",
                    items: [
                        {
                            id: "translate-to-english",
                            title: "English",
                            icon: "#translate",
                        },
                        {
                            id: "translate-to-turkish",
                            title: "Türkçe",
                            icon: "#translate",
                        },
                        {
                            id: "translate-to-arabic",
                            title: "العربية",
                            icon: "#translate",
                        },
                        {
                            id: "translate-to-deutsch",
                            title: "Deutsch",
                            icon: "#translate",
                        },
                        {
                            id: "translate-to-russian",
                            title: "Русский",
                            icon: "#translate",
                        },
                        {
                            id: "translate-to-spanish",
                            title: "Español",
                            icon: "#translate",
                        },
                    ],
                },
            ],
        });

        return {
            items,
            isLoading: false,
        };
    },
    methods: {
        closeRadialMenu() {
            this.$emit("close");
            this.$parent?.$refs?.messageArea?.$refs?.editor?.closeAITools(true);
        },
        stripHtmlTags(input) {
            return input.replace(/<\/?[^>]+(>|$)/g, "");
        },
        async radialMenuClicked(menuItem) {
            this.isLoading = true;
            try {
                const position = { ...this.payload.position };
                let message = this.payload.message;
                if (this.type === "mail") {
                    message = this.stripHtmlTags(this.mailText);
                }
                if (this.type === "ticket") {
                    message = this.ticketText;
                }
                let text = `${message}`;
                const copilot = CoPilot.getInstance();

                if (menuItem.id === "fix-grammar") {
                    const response = await copilot.fixGrammar(message, this.copilotContextOpts);
                    this.$message.success(this.$t("message.grammarFixed"));
                    text = response;
                } else if (menuItem.id === "long-sentence") {
                    const response = await copilot.longSentence(message, this.copilotContextOpts);
                    this.$message.success(this.$t("message.messageLonger"));
                    text = response;
                } else if (menuItem.id === "ai-response") {
                    const response = await copilot.recommendAnswer(
                        this.site._id,
                        this.user._id,
                        this.contact?.lastConversation?._id,
                    );
                    this.$message.success(this.$t("message.aiResponseAdded"));
                    text = response;
                } else if (menuItem.id === "ticket-ai-response") {
                    const response = await copilot.ticketAiresponse(this.site._id, this.user._id, this.ticketMessage, this.osrPayload);
                    this.$message.success(this.$t("message.aiResponseAdded"));
                    text = response;
                } else if (menuItem.id.startsWith("change-tone-to-")) {
                    const tone = menuItem.id.replace("change-tone-to-", "");
                    const response = await copilot.changeTone(message, tone, this.copilotContextOpts);
                    this.$message.success(this.$t("message.toneChanged", { tone: tone }));
                    text = response;
                } else if (menuItem.id === "mail-ai-response") {
                    const response = await copilot.mailAiresponse(this.site._id, this.user._id, this.mailDetails);
                    this.$message.success(this.$t("message.aiResponseAdded"));
                    text = response;
                } else if (menuItem.id === "mail-prompt-message") {
                    let mailText = this.stripHtmlTags(this.mailText);
                    const response = await copilot.mailPromtMessage(
                        this.site._id,
                        this.user._id,
                        this.mailDetails,
                        mailText,
                    );
                    this.$message.success("Cevap kaydedildi.");
                    text = response;
                } else if (menuItem.id.startsWith("translate")) {
                    const language = menuItem.id.replace("translate-to-", "");
                    if (language === "my-language") {
                        const translateCode = this.$_.get(this.user, "lang", this.$_.get(this.language, "code", "en"));
                        const response = await copilot.messageTranslation(message, translateCode, this.copilotContextOpts);
                        this.$message.success(this.$t("message.translateMessage"));
                        text = response;
                    } else if (language === "its-language") {
                        const translateCode = this.contact.preferredLang || "en";
                        const response = await copilot.messageTranslation(message, translateCode, this.copilotContextOpts);
                        this.$message.success(this.$t("message.translateMessage"));
                        text = response;
                    } else {
                        let translateCode = "tr";
                        if (language === "english") translateCode = "en";
                        if (language === "turkish") translateCode = "tr";
                        if (language === "arabic") translateCode = "ar";
                        if (language === "deutsch") translateCode = "de";
                        if (language === "russian") translateCode = "ru";
                        if (language === "spanish") translateCode = "es";
                        const response = await copilot.messageTranslation(message, translateCode, this.copilotContextOpts);
                        this.$message.success(this.$t("message.translateMessage"));
                        text = response;
                    }
                } else if (menuItem.id === "prompt-message") {
                    const response = await copilot.promptToGenerateResponse(
                        this.site._id,
                        this.user._id,
                        this.contact?.lastConversation?._id,
                        message,
                    );
                    this.$message.success("Cevap kaydedildi.");
                    text = response;
                }
                this.$emit("change", text);

                this.$parent?.$refs?.messageArea?.$refs?.editor?.setContent(text, position);
                this.closeRadialMenu();
            } catch (error) {
                this.$message.error('AI işlemi sırasında bir hata oluştu.');
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.ai-copilot {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 9;
    background: rgba(255, 255, 255, 0.65);
    width: 100%;
    height: 100%;
}
</style>
